import {
  Button,
  DropzoneReadonly,
  FiFileText,
  FiTrash2,
  Flex,
  GridItem,
  ReadOnlyField,
  useFormContext,
} from '@agrotoken-tech/ui';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import { S3FileUploader } from '@bcba/frontend-utils';
import { useTranslation } from '@bcba/react-hooks';
import { FileMimeType } from '@bcba/types';
import { useEffect, useMemo, useState } from 'react';
import { getUrlFileFromAws } from '../../services/aws/getUrlFileFromAws';
import { getS3SignedUrl } from '../../services/documents/getS3SignedUrl';

interface Props {
  documentName: string;
  isDocumentRequired?: boolean;
  label: string;
  mt?: Parameters<typeof GridItem>[0]['mt'];
  handleRemove?: () => void;
  hideDelete?: boolean;
  isReadonly?: boolean;
  helperText?: string;
  expirationDateHelperText?: string;
  defaultReadOnlyName?: string;
}

export const DocumentSection = ({
  documentName,
  isDocumentRequired = false,
  label,
  mt,
  hideDelete = false,
  handleRemove,
  isReadonly = false,
  helperText,
  expirationDateHelperText,
  defaultReadOnlyName,
}: Props) => {
  const { t } = useTranslation();
  const toBeCompletedByMessage = t('common.toBeCompletedByBolsa');
  const { setValue, watch, unregister, resetField } = useFormContext();
  const [fileName, setFileName] = useState('');
  const documentUuidKey = `${documentName}.documentUuid`;
  const uuidKey = `${documentName}.uuid`;
  const shouldShowReadOnlyDropzoneField =
    isReadonly || watch(`${documentName}.uuid`);
  const handleDeleteFileClick = () => {
    setValue(uuidKey, undefined, {
      shouldDirty: true,
    });
  };
  const keepOriginal = watch(`${documentName}.keepOriginal`);

  const identifierValue =
    watch(`${documentName}.identifier`) || toBeCompletedByMessage;
  const expirationDateValue = watch(`${documentName}.expirationDate`);

  const hasHandleDelete = Boolean(handleRemove);
  const labelWithRemoveButton = useMemo(() => {
    return hasHandleDelete ? (
      <Flex justifyContent={'space-between'} w="full">
        {label}
        <Button
          fontSize={'14px'}
          h={'14px'}
          w={'14px !important'}
          rightIcon={<FiTrash2 />}
          onClick={handleRemove}
          variant="linkDestructive"
        >
          {t('common.delete')}
        </Button>
      </Flex>
    ) : (
      label
    );
  }, [hasHandleDelete]) as unknown as string;

  useEffect(() => {
    const originalFileNameFromRHF = watch(`${documentName}.originalName`);
    if (keepOriginal && originalFileNameFromRHF) {
      setFileName(originalFileNameFromRHF);
      unregister(`${documentName}.originalName`);
    }
  }, [keepOriginal]);

  return (
    <>
      <GridItem colSpan={{ base: 1, lg: 2 }} mt={mt} position={'relative'}>
        {shouldShowReadOnlyDropzoneField ? (
          <DropzoneReadonly
            fileName={
              fileName
                ? fileName
                : defaultReadOnlyName ?? t('common.fileUpload.disabled')
            }
            label={labelWithRemoveButton}
            onClick={async () => {
              const uuid = watch(documentUuidKey);
              if (uuid) {
                const url = await getUrlFileFromAws(watch(documentUuidKey));
                window.open(url, '_blank');
              }
            }}
            leftIcon={FiFileText}
            {...(hideDelete
              ? { onDelete: undefined }
              : { onDelete: () => handleDeleteFileClick() })}
          />
        ) : (
          <S3FileUploader
            fieldPrefix={`${documentName}.file.s3TempKey`}
            label={labelWithRemoveButton}
            getS3PreSignedUrl={getS3SignedUrl}
            onFileUploadSuccess={async (fileName, mimeType, s3TempKey) => {
              setValue(`${documentName}.file.name`, fileName);
              setValue(
                `${documentName}.file.mimeType`,
                mimeType as FileMimeType,
              );
              setValue(`${documentName}.file.s3TempKey`, s3TempKey);
              setValue(`${documentName}.keepOriginal`, false, {
                shouldDirty: true,
              });
            }}
            onDeleteClick={() => {
              resetField(`${documentName}.file.s3TempKey`);
              resetField(`${documentName}.file.name`);
              resetField(`${documentName}.file.mimeType`);
            }}
            required={isDocumentRequired}
            helperText={helperText}
          />
        )}
      </GridItem>

      <ReadOnlyField
        label={t('common.documentId.label')}
        value={identifierValue}
      />
      <ReadOnlyField
        label={t('common.expirationDate.label')}
        value={
          expirationDateValue
            ? formatDateToShortFormat(unixTimestampToDate(expirationDateValue))
            : toBeCompletedByMessage
        }
        helperText={expirationDateHelperText}
      />
    </>
  );
};
